import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeleteSignatureRole, ProjectsLoad, SignatureRoleLoad } from '@completion/actions';
import { Confirmation, SignatureRole } from '@completion/models';
import { getCurrentProject, getSignatureRoles } from '@completion/selectors';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog';
import { SignatureRoleEditDialogComponent } from '../signature-role-edit-dialog/signature-role-edit-dialog.component';

@Component({
  selector: 'app-signature-role',
  templateUrl: './signature-role.component.html',
  styleUrls: ['./signature-role.component.scss'],
  standalone: false,
})
export class SignatureRoleComponent implements OnInit {
  dataSource: SignatureRole[] = [];
  displayedColumns = ['name', 'description', 'groupEmail', 'edit', 'delete'];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly dialog: MatDialog,
  ) {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new SignatureRoleLoad());
        } else {
          this.store.dispatch(new ProjectsLoad());
        }
      });
  }

  ngOnInit(): void {
    this.store
      .select(getSignatureRoles)
      .pipe(takeUntil(this.destroy$))
      .subscribe((signatureRoles) => {
        if (signatureRoles) {
          this.dataSource = signatureRoles;
        }
      });
  }

  // Compute the next ordinal based on existing signature roles
  private getNextOrdinal(): number {
    if (!this.dataSource || this.dataSource.length === 0) {
      return 0;
    }
    const ordinals = this.dataSource
      .map((r) => (typeof (r as any).ordinal === 'number' ? ((r as any).ordinal as number) : null))
      .filter((v): v is number => v !== null && !isNaN(v));
    if (ordinals.length === 0) {
      return 0;
    }
    return Math.max(...ordinals) + 1;
  }

  isLoading() {
    return false;
  }

  addSignatureRole() {
    const nextOrdinal = this.getNextOrdinal();
    const newRole = {
      id: null,
      name: '',
      description: '',
      groupEmail: '',
      // assign ordinal for new role: last ordinal + 1 (or 0 if none)
      ordinal: nextOrdinal,
    } as SignatureRole;

    this.dialog
      .open(SignatureRoleEditDialogComponent, {
        data: {
          signatureRole: newRole,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          // no-op here; add/update is handled inside the dialog via store actions
        }
      });
  }

  editSignatureRole(signatureRole: SignatureRole) {
    this.dialog
      .open(SignatureRoleEditDialogComponent, {
        data: {
          signatureRole: signatureRole,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          // no-op; update is handled in the dialog
        }
      });
  }

  deleteSignatureRole(signatureRole: SignatureRole) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Signature Role',
          message: `Are you sure you want to delete ${signatureRole.name}?`,
          confirmationTitle: 'Delete',
          cancelTitle: 'Cancel',
          isCancelDisplay: true,
        } as Confirmation,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new DeleteSignatureRole(signatureRole));
        }
      });
  }
}
